import { useState, useRef, useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import api from 'app/shared/utils/api';
import adapterUtils from 'app/shared/utils/adapterUtils';
import constants from 'app/shared/constants/DispatchConstants';
import { adapt_reduxToJava } from 'app/shared/flux/actions/FilterActions/adapters';
import type { RootReduxState } from 'app/shared/flux/reducers';

const PAGE_SIZE = 40;
const MAX_LISTINGS = 500;

export const useInfiniteListings = (initialListings: Array<Record<string, unknown>>, totalListings: number) => {
  const dispatch = useDispatch();
  const filter = useSelector((state: RootReduxState) => state.filter);
  const areaData = useSelector((state: RootReduxState) => state.area.area);

  const [allListings, setAllListings] = useState<Array<Record<string, unknown>>>(initialListings);
  const paginationRef = useRef({
    offset: initialListings.length,
    isFetching: false,
    hasMore: initialListings.length < totalListings && initialListings.length < MAX_LISTINGS,
  });

  // Stable fingerprint for initialListings to avoid infinite re-render loops
  // when the parent creates a new [] reference on every render
  const initialListingsFingerprint =
    initialListings.length > 0
      ? `${initialListings.length}:${(initialListings[0] as { maloneLotIdEncoded?: string }).maloneLotIdEncoded}`
      : 'empty';

  // Reset when initialListings changes (overlay reopen or new SRP navigation)
  useEffect(() => {
    setAllListings(initialListings);
    paginationRef.current = {
      offset: initialListings.length,
      isFetching: false,
      hasMore: initialListings.length < totalListings && initialListings.length < MAX_LISTINGS,
    };
  }, [initialListingsFingerprint, totalListings]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchNextPage = useCallback(async () => {
    const pagination = paginationRef.current;
    if (pagination.isFetching || !pagination.hasMore) return;

    if (!areaData || !areaData.maxLat || !areaData.maxLon || !areaData.minLat || !areaData.minLon) return;

    pagination.isFetching = true;
    // Trigger a re-render so isFetching is visible
    setAllListings((prev) => [...prev]);

    try {
      const apiParams = adapt_reduxToJava({
        filter,
        area: areaData as unknown as Parameters<typeof adapt_reduxToJava>[0]['area'],
        offset: pagination.offset,
        limit: PAGE_SIZE,
      });

      const result: { data?: { buildings?: unknown[]; numUnits?: number } } = await (dispatch as Function)(
        api.listing.fetchByCoords(apiParams, null),
      );

      if (result?.data?.buildings) {
        const processedListings = adapterUtils.apiLimitedBuildingArrayToSummaryArray(
          result.data.buildings,
          filter,
          false,
        );

        setAllListings((prev) => {
          const existingIds = new Set(prev.map((l) => (l as { maloneLotIdEncoded?: string }).maloneLotIdEncoded));
          const newListings = processedListings.filter(
            (l: Record<string, unknown>) => !existingIds.has((l as { maloneLotIdEncoded?: string }).maloneLotIdEncoded),
          );
          const combined = [...prev, ...newListings];
          const capped = combined.length > MAX_LISTINGS ? combined.slice(0, MAX_LISTINGS) : combined;

          // Dispatch to Redux so map pins update
          if (newListings.length > 0) {
            dispatch({
              type: constants.APPEND_LISTINGS_BY_COORDS,
              payload: { listings: newListings },
            });
          }

          const apiReturnedFewer = processedListings.length < PAGE_SIZE;
          const reachedCap = capped.length >= MAX_LISTINGS;
          const reachedTotal = capped.length >= totalListings;

          paginationRef.current = {
            offset: pagination.offset + processedListings.length,
            isFetching: false,
            hasMore: !apiReturnedFewer && !reachedCap && !reachedTotal,
          };

          return capped;
        });
      } else {
        paginationRef.current = { ...pagination, isFetching: false, hasMore: false };
        setAllListings((prev) => [...prev]);
      }
    } catch {
      paginationRef.current = { ...pagination, isFetching: false };
      setAllListings((prev) => [...prev]);
    }
  }, [dispatch, filter, areaData, totalListings]);

  return {
    allListings,
    isFetching: paginationRef.current.isFetching,
    hasMore: paginationRef.current.hasMore,
    fetchNextPage,
  };
};
